// Filename: BlobPlugin.js
import React, { useState, useEffect } from 'react';

const BlobPlugin = () => {
  // State for FAQ toggles
  const [activeFaq, setActiveFaq] = useState(null);
  // State for live blob preview
  const [blobStyle, setBlobStyle] = useState({});

  // Sophisticated color palette based on a professional interpretation of your brand
  const colors = {
    primaryDark: '#0a1a2b',      // Deep navy - stable, trustworthy
    primaryMedium: '#1e3a5f',     // Medium blue - professional
    accentAmber: '#b86824',       // Refined amber - sophisticated contrast
    accentAmberLight: '#d98f4a',  // Lighter amber for hovers
    neutralGray: '#4a5568',       // Medium gray for text
    neutralLight: '#f8fafc',      // Off-white for backgrounds
    borderLight: '#e9edf2',       // Subtle border color
    successGreen: '#2e7d5e',      // Muted green for positive indicators
  };

  // Live blob animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      // Generate random blob shape values
      const r1 = Math.floor(Math.random() * 40 + 30); // 30-70%
      const r2 = Math.floor(Math.random() * 40 + 30);
      const r3 = Math.floor(Math.random() * 40 + 30);
      const r4 = Math.floor(Math.random() * 40 + 30);
      const r5 = Math.floor(Math.random() * 40 + 30);
      const r6 = Math.floor(Math.random() * 40 + 30);
      const r7 = Math.floor(Math.random() * 40 + 30);
      const r8 = Math.floor(Math.random() * 40 + 30);
      
      setBlobStyle({
        borderRadius: `${r1}% ${r2}% ${r3}% ${r4}% / ${r5}% ${r6}% ${r7}% ${r8}%`,
        transition: 'border-radius 2s ease-in-out',
      });
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);

  // Inline styles object
  const styles = {
    pageContainer: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: colors.primaryDark,
      lineHeight: 1.6,
      backgroundColor: '#ffffff',
    },
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '0 24px',
    },
    section: {
      padding: '80px 0',
    },
    sectionLight: {
      backgroundColor: colors.neutralLight,
    },
    sectionTitle: {
      fontSize: 'clamp(2rem, 4vw, 2.8rem)',
      fontWeight: '600',
      lineHeight: 1.2,
      marginBottom: '20px',
      color: colors.primaryDark,
      letterSpacing: '-0.02em',
    },
    sectionSubtitle: {
      fontSize: '1.2rem',
      color: colors.neutralGray,
      maxWidth: '800px',
      margin: '0 auto 50px auto',
      fontWeight: '400',
    },
    // Hero Section - More refined gradient
    heroSection: {
      background: `linear-gradient(145deg, ${colors.primaryDark} 0%, ${colors.primaryMedium} 100%)`,
      color: '#ffffff',
      padding: '100px 0',
      position: 'relative',
      overflow: 'hidden',
    },
    heroContent: {
      maxWidth: '800px',
      margin: '0 auto',
      textAlign: 'center',
      position: 'relative',
      zIndex: 2,
    },
    heroTitle: {
      fontSize: 'clamp(2.8rem, 6vw, 4.5rem)',
      fontWeight: '700',
      marginBottom: '25px',
      letterSpacing: '-0.02em',
      lineHeight: 1.1,
    },
    heroSubtitle: {
      fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
      opacity: 0.9,
      marginBottom: '40px',
      fontWeight: '400',
    },
    // Button styles - Professional and clean
    buttonPrimary: {
      backgroundColor: colors.accentAmber,
      color: '#ffffff',
      border: 'none',
      padding: '16px 42px',
      fontSize: '1.1rem',
      fontWeight: '600',
      borderRadius: '60px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 8px 20px -5px rgba(184, 104, 36, 0.3)',
      display: 'inline-block',
      textDecoration: 'none',
      letterSpacing: '0.3px',
    },
    buttonSecondary: {
      backgroundColor: 'transparent',
      color: '#ffffff',
      border: '2px solid rgba(255,255,255,0.3)',
      padding: '14px 36px',
      fontSize: '1.1rem',
      fontWeight: '500',
      borderRadius: '60px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'inline-block',
      textDecoration: 'none',
      marginLeft: '20px',
    },
    buttonOutline: {
      backgroundColor: 'transparent',
      color: colors.primaryDark,
      border: `2px solid ${colors.accentAmber}`,
      padding: '14px 32px',
      fontSize: '1rem',
      fontWeight: '600',
      borderRadius: '60px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'inline-block',
      textDecoration: 'none',
    },
    // Card styles
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '24px',
      boxShadow: '0 20px 35px -8px rgba(10, 26, 43, 0.1)',
      padding: '40px',
      border: `1px solid ${colors.borderLight}`,
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    },
    featureGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '30px',
      marginTop: '40px',
    },
    featureCard: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      padding: '32px',
      boxShadow: '0 10px 25px -8px rgba(10, 26, 43, 0.08)',
      border: `1px solid ${colors.borderLight}`,
      transition: 'all 0.3s ease',
    },
    // Blob preview styles
    blobShowcase: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '40px',
      marginTop: '40px',
    },
    blobCard: {
      background: '#ffffff',
      borderRadius: '20px',
      padding: '30px',
      textAlign: 'center',
      boxShadow: '0 15px 30px -10px rgba(10, 26, 43, 0.15)',
      border: `1px solid ${colors.borderLight}`,
    },
    blobWrapper: {
      width: '250px',
      height: '250px',
      margin: '0 auto 20px',
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
      animation: 'blobAnimation 8s ease-in-out infinite',
      boxShadow: '0 20px 30px -10px rgba(0,0,0,0.2)',
    },
    blobWrapperSmall: {
      width: '100px',
      height: '100px',
      margin: '0 auto',
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
      animation: 'blobAnimation 8s ease-in-out infinite',
    },
    blobImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    blobVideo: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      background: `linear-gradient(45deg, ${colors.accentAmber}, ${colors.primaryMedium})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '3em',
    },
    badge: {
      display: 'inline-block',
      background: colors.successGreen,
      color: 'white',
      padding: '6px 16px',
      borderRadius: '50px',
      fontSize: '0.9rem',
      fontWeight: '600',
      marginTop: '15px',
    },
    elementorBadge: {
      background: colors.accentAmber,
      marginLeft: '10px',
    },
    // Code block
    codeBlock: {
      backgroundColor: colors.primaryDark,
      color: '#e2e8f0',
      padding: '24px',
      borderRadius: '16px',
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      fontSize: '0.95rem',
      overflowX: 'auto',
      margin: '30px 0',
      border: `1px solid ${colors.primaryMedium}`,
    },
    // Stats
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '40px',
      maxWidth: '1000px',
      margin: '50px auto 0',
      textAlign: 'center',
    },
    statItem: {
      padding: '20px',
    },
    statNumber: {
      fontSize: '3.2rem',
      fontWeight: '700',
      color: colors.primaryDark,
      marginBottom: '8px',
      lineHeight: 1,
    },
    statLabel: {
      fontSize: '0.95rem',
      textTransform: 'uppercase',
      letterSpacing: '1.5px',
      color: colors.neutralGray,
      fontWeight: '500',
    },
    // Divider
    divider: {
      height: '1px',
      background: `linear-gradient(90deg, transparent, ${colors.borderLight}, transparent)`,
      margin: '60px 0',
    },
    // Live blob demo
    liveBlob: {
      width: '300px',
      height: '300px',
      background: `linear-gradient(135deg, ${colors.accentAmber}, ${colors.primaryMedium})`,
      margin: '0 auto',
      ...blobStyle,
      boxShadow: '0 30px 40px -15px rgba(0,0,0,0.3)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '1.5rem',
      fontWeight: '600',
    },
  };

  // Handle download
  const handleDownload = () => {
    // Pointing to the assets folder at the root of your React project
    const pluginDownloadUrl = '/download/animated-blob-media.zip';
    
    const link = document.createElement('a');
    link.href = pluginDownloadUrl;
    link.download = 'animated-blob-media.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('Plugin download initiated from assets folder');
  };

  // Toggle FAQ
  const toggleFaq = (index) => {
    if (activeFaq === index) {
      setActiveFaq(null);
    } else {
      setActiveFaq(index);
    }
  };

  // Add keyframe animations to document
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes blobAnimation {
        0%, 100% { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
        25% { border-radius: 58% 42% 75% 25% / 76% 46% 54% 24%; }
        50% { border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%; }
        75% { border-radius: 33% 67% 58% 42% / 63% 68% 32% 37%; }
      }
      
      @keyframes floatAnimation {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-15px); }
      }
      
      .float-animation {
        animation: floatAnimation 6s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={styles.pageContainer}>
      {/* Professional Hero Section with Live Blob Preview */}
      <section style={styles.heroSection}>
        <div style={styles.container}>
          <div style={styles.heroContent}>
            <h1 style={styles.heroTitle}>
              Animated Blob Media
              <span style={{ display: 'block', fontSize: '0.4em', fontWeight: '400', opacity: 0.8, marginTop: '10px' }}>
                by Dot LocalHost
              </span>
            </h1>
            <p style={styles.heroSubtitle}>
              A professional, lightweight WordPress plugin to display images and videos 
              inside smooth, CSS-animated blob shapes. Fully responsive and Elementor compatible.
            </p>
            
            {/* Live Blob Preview */}
            <div style={{ margin: '40px auto', maxWidth: '300px' }}>
              <div style={styles.liveBlob} className="float-animation">
                Live Demo
              </div>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', marginTop: '15px' }}>
                ✨ Blob shape changes every 2 seconds
              </p>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
              <button 
                onClick={handleDownload} 
                style={styles.buttonPrimary}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.accentAmberLight}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.accentAmber}
              >
                Download Plugin v1.0
              </button>
              <button 
                style={styles.buttonSecondary}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#ffffff'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'}
                onClick={() => document.getElementById('showcase').scrollIntoView({ behavior: 'smooth' })}
              >
                View Showcase
              </button>
            </div>
          </div>
        </div>
        {/* Subtle abstract shape for depth */}
        <div style={{
          position: 'absolute',
          bottom: '-50px',
          right: '-50px',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: 1,
        }} />
      </section>

      {/* Live Plugin Showcase Section */}
      <section style={styles.section} id="showcase">
        <div style={styles.container}>
          <h2 style={{...styles.sectionTitle, textAlign: 'center'}}>See It In Action</h2>
          <p style={styles.sectionSubtitle}>
            Watch how your images and videos transform into living, breathing shapes
          </p>
          
          <div style={styles.blobShowcase}>
            {/* Image Blob Example */}
            <div style={styles.blobCard}>
              <div style={styles.blobWrapper}>
                <img 
                  style={styles.blobImage}
                  src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400"
                  alt="Mountain landscape in blob shape"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400/1e3a5f/ffffff?text=Image+Blob';
                  }}
                />
              </div>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>Image in Blob</h3>
              <div>
                <span style={styles.badge}>JPG / PNG / GIF</span>
              </div>
            </div>
            
            {/* Video Blob Example */}
            <div style={styles.blobCard}>
              <div style={styles.blobWrapper}>
                <video 
                  style={styles.blobImage}
                  autoPlay
                  loop
                  muted
                  playsInline
                  poster="https://via.placeholder.com/400/1e3a5f/ffffff?text=Video+Blob"
                >
                  <source src="https://samplelib.com/lib/preview/mp4/sample-5s.mp4" type="video/mp4" />
                  <div style={styles.blobVideo}>▶️</div>
                </video>
              </div>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>Video in Blob</h3>
              <div>
                <span style={styles.badge}>MP4 / WebM</span>
              </div>
            </div>
            
            {/* Multiple Blobs Example */}
            <div style={styles.blobCard}>
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginBottom: '20px' }}>
                <div style={{...styles.blobWrapperSmall, animationDelay: '0s'}}>
                  <img 
                    style={styles.blobImage}
                    src="https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=200"
                    alt="Profile in blob"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/200/1e3a5f/ffffff?text=Profile';
                    }}
                  />
                </div>
                <div style={{...styles.blobWrapperSmall, animationDelay: '-2s', background: colors.accentAmber}}>
                  <div style={{...styles.blobImage, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '2rem' }}>
                    🎨
                  </div>
                </div>
                <div style={{...styles.blobWrapperSmall, animationDelay: '-4s', background: colors.primaryMedium}}>
                  <div style={{...styles.blobImage, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '2rem' }}>
                    ✨
                  </div>
                </div>
              </div>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>Multiple Blobs</h3>
              <div>
                <span style={{...styles.badge, ...styles.elementorBadge}}>Elementor Widget</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Features Grid */}
      <section style={{...styles.section, ...styles.sectionLight}}>
        <div style={styles.container}>
          <h2 style={{...styles.sectionTitle, textAlign: 'center'}}>Enterprise-Grade Features</h2>
          <p style={styles.sectionSubtitle}>
            Built for developers, designers, and agencies who demand quality and performance.
          </p>
          
          <div style={styles.featureGrid}>
            {[
              { title: 'Shortcode Support', desc: 'Easy [blob_media] shortcode with full attribute support for quick embedding anywhere.' },
              { title: 'Video & Image Support', desc: 'Works with MP4, WebM, JPG, PNG, GIF. Autoplay options for videos.' },
              { title: 'Responsive Controls', desc: 'Set different blob sizes and shapes for mobile, tablet, and desktop.' },
              { title: 'Developer Friendly', desc: 'Clean code, hooks, and filters for advanced customizations.' },
            ].map((feature, idx) => (
              <div key={idx} style={styles.featureCard}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 20px 30px -10px rgba(10, 26, 43, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 25px -8px rgba(10, 26, 43, 0.08)';
                }}
              >
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '15px', color: colors.primaryDark }}>
                  {feature.title}
                </h3>
                <p style={{ color: colors.neutralGray, lineHeight: 1.7 }}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Documentation / Usage Section */}
      <section style={styles.section} id="documentation">
        <div style={styles.container}>
          <div style={styles.card}>
            <h2 style={{...styles.sectionTitle, marginBottom: '30px'}}>Simple Integration</h2>
            <p style={{ marginBottom: '20px', fontSize: '1.1rem' }}>
              Use the shortcode in any post, page, or widget. Advanced attributes give you full control.
            </p>
            
            <h3 style={{ fontSize: '1.3rem', fontWeight: '600', marginTop: '30px', marginBottom: '15px' }}>Image Blob</h3>
            <div style={styles.codeBlock}>
              [blob_media src="/wp-content/uploads/image.jpg" type="image" width="400" height="400" blob_intensity="1.2"]
            </div>
            
            <h3 style={{ fontSize: '1.3rem', fontWeight: '600', marginTop: '30px', marginBottom: '15px' }}>Video Blob (with autoplay)</h3>
            <div style={styles.codeBlock}>
              [blob_media src="/wp-content/uploads/video.mp4" type="video" width="500" height="500" autoplay="true" loop="true"]
            </div>
            
            <p style={{ marginTop: '20px', fontStyle: 'italic', color: colors.neutralGray }}>
              * All attributes are optional. Default blob intensity provides a smooth, subtle animation.
            </p>
            
            <div style={styles.divider} />
            
            {/* FAQ Section */}
            <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '20px' }}>Frequently Asked Questions</h3>
            {[
              { q: 'Is this compatible with the latest WordPress version?', a: 'Yes, Animated Blob Media is tested with WordPress 6.x and follows all coding standards.' },
              { q: 'Does it work with page builders other than Elementor?', a: 'The shortcode works with any page builder (Gutenberg, WPBakery, Divi, etc.). The Elementor widget provides a visual interface.' },
              { q: 'Can I use multiple blobs on the same page?', a: 'Absolutely! Use as many shortcodes as you need. Each instance is independent and lightweight.' },
            ].map((item, idx) => (
              <div key={idx} style={{ borderBottom: `1px solid ${colors.borderLight}`, marginBottom: '15px' }}>
                <button 
                  onClick={() => toggleFaq(idx)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '15px 0',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    color: colors.primaryDark,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {item.q}
                  <span style={{ fontSize: '1.5rem' }}>{activeFaq === idx ? '−' : '+'}</span>
                </button>
                {activeFaq === idx && (
                  <p style={{ padding: '0 0 20px 0', color: colors.neutralGray, margin: 0 }}>
                    {item.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Stats / Trust Section */}
      <section style={{...styles.section, ...styles.sectionLight}}>
        <div style={styles.container}>
          <div style={styles.statsGrid}>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>2,500+</div>
              <div style={styles.statLabel}>Active Installs</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>5★</div>
              <div style={styles.statLabel}>Average Rating</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>24/7</div>
              <div style={styles.statLabel}>Premium Support</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>GPL2</div>
              <div style={styles.statLabel}>Licensed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Final CTA */}
      <section style={{...styles.section, textAlign: 'center'}}>
        <div style={styles.container}>
          <h2 style={{...styles.sectionTitle, marginBottom: '20px'}}>Elevate Your Website Today</h2>
          <p style={{...styles.sectionSubtitle, marginBottom: '40px', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto'}}>
            Join hundreds of professionals using Animated Blob Media to create memorable visual experiences.
          </p>
          <button 
            onClick={handleDownload} 
            style={styles.buttonPrimary}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.accentAmberLight}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.accentAmber}
          >
            Download Plugin (Free)
          </button>
          <p style={{ marginTop: '30px', fontSize: '0.9rem', color: colors.neutralGray }}>
            Version 1.0 | GPL2 Licensed | By Dot LocalHost | <a href="#" style={{ color: colors.accentAmber, textDecoration: 'none' }}>Terms</a> & <a href="#" style={{ color: colors.accentAmber, textDecoration: 'none' }}>Privacy</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default BlobPlugin;